/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, November 30th 2022, 6:23:18 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Typography from "@mui/material/Typography";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { COLORSCHEME } from "theme/styles";
import { getPurchaseFlowStrategyHook } from "saas-subscription/strategy";
import { SubscriptionPlan } from "saas-subscription/schemas";
import { getPriceFromInterval, getUnitAmount } from "saas-subscription/utils";

interface SubscriptionPlanCardProps {
  subscriptionPlan: SubscriptionPlan;
  handlePlanSelection: (subscriptionPlan: SubscriptionPlan) => void;
  priceInterval: SubscriptionPlan["prices"][number]["recurring"]["interval"];
}

export const SubscriptionPlanCard = ({
  subscriptionPlan,
  handlePlanSelection,
  priceInterval,
}: SubscriptionPlanCardProps) => {
  const usePurchaseFlowStrategy = getPurchaseFlowStrategyHook(subscriptionPlan);
  const strategy = usePurchaseFlowStrategy();
  const displayPrice = getPriceFromInterval({ subscriptionPlan, priceInterval });
  const subscriptionPlanMetadata = subscriptionPlan?.metadata;
  const { shortName, graphic, packageColor } = strategy.getWidgets?.(subscriptionPlan) ?? {};
  const bestValueClassName = subscriptionPlan.metadata?.is_best_value ? "plan-best-value" : "";
  const purchaseActions = strategy.getPurchaseCTA({
    onClickPlan: () => handlePlanSelection(subscriptionPlan),
    subscriptionPlan,
  });

  return (
    <Card className={`os-subscription-widget ${bestValueClassName}`}>
      <PlanCardHeader title={shortName} isBestValue={Boolean(subscriptionPlanMetadata?.is_best_value)} />

      <CardContent>
        <PlanCardPrice subscriptionPlan={subscriptionPlan} displayPrice={displayPrice} packageColor={packageColor} />
      </CardContent>

      <CardActions>{purchaseActions}</CardActions>

      <CardContent>
        <Stack spacing={2}>
          <PlanCardGraphic graphic={graphic} />
          <PlanCardFeatures subscriptionPlan={subscriptionPlan} />
        </Stack>
      </CardContent>
    </Card>
  );
};

const PlanCardHeader = ({ title, isBestValue }: { title: string; isBestValue: boolean }) => {
  const subheader = isBestValue ? "Best Value" : null;

  return (
    <CardHeader
      title={title}
      subheader={subheader}
      titleTypographyProps={{ align: "center" }}
      subheaderTypographyProps={{
        align: "center",
        color: COLORSCHEME.onSecondary,
      }}
      sx={{
        color: COLORSCHEME.onSecondary,
      }}
    />
  );
};

const PlanCardGraphic = ({ graphic }: { graphic: React.ReactNode }) => {
  return graphic && <div style={{ textAlign: "center" }}>{graphic}</div>;
};

const PlanCardPrice = ({
  subscriptionPlan,
  displayPrice,
  packageColor,
}: Pick<SubscriptionPlanCardProps, "subscriptionPlan"> & {
  displayPrice: SubscriptionPlan["prices"][0];
  packageColor: string;
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "baseline",
        mb: 2,
      }}
    >
      {subscriptionPlan?.metadata?.product_code === "onestop-enterprise" ? (
        <>
          <SupportAgentIcon color="primary" fontSize="large" />
          <Typography variant="h6" color="text.secondary">
            Contact Us
          </Typography>
        </>
      ) : (
        <>
          <Typography component="h2" variant="h3" color={packageColor}>
            {getUnitAmount({ price: displayPrice, withCurrency: true })}
          </Typography>
          <Typography variant="h6" color="text.secondary">
            {`/${subscriptionPlan.unit_label ?? ""} ${displayPrice.recurring?.interval === "month" ? "/mo" : "/yr"}`}
          </Typography>
        </>
      )}
    </Box>
  );
};

const PlanCardFeatures = ({ subscriptionPlan }: Pick<SubscriptionPlanCardProps, "subscriptionPlan">) => {
  return (
    <Stack spacing={2} className="os-package-features-widget">
      {subscriptionPlan.features?.map((line) => (
        <Typography component="p" variant="subtitle1" align="center" key={line}>
          {line}
        </Typography>
      ))}
    </Stack>
  );
};
